import companyUserService from '@/service/CompanyUserServices/CompanyUserService.js';
import userAuthService from '@/service/userAuthService';

const USERTYPE_CO_OWNER = 'usertype05';
const MANAGE_OWNED_ASSETS_MODULE_CODE = '2006';
const MANAGE_OWNED_ASSETS_ROUTE = 'assetDocumentProcess';
const MANAGE_OWNED_ASSETS_POST_LOGIN_ROUTE = '/assetDocumentProcess';
const CACHE_STORAGE_KEY = 'coOwnerOwnedAssetsMenuCache';
const MENU_UPDATED_EVENT = 'co-owner-menu-updated';

function normalizeText(value) {
    return String(value || '').trim().toLowerCase();
}

function normalizeRoute(route) {
    const raw = normalizeText(route);
    if (!raw) {
        return '';
    }
    return raw.replace(/^\/+/, '');
}

export function getLoggedInUserType() {
    try {
        const fromStorage = localStorage.getItem('userType');
        if (fromStorage) {
            return normalizeText(fromStorage);
        }
        const userinfoRaw = localStorage.getItem('userinfo');
        if (!userinfoRaw) {
            return '';
        }
        const parsed = JSON.parse(userinfoRaw);
        return normalizeText(parsed?.data?.userType);
    } catch {
        return '';
    }
}

export function isCoOwnerUserType(userType) {
    const resolved = userType != null && userType !== ''
        ? normalizeText(userType)
        : getLoggedInUserType();
    return resolved === USERTYPE_CO_OWNER;
}

/** Married Parents and Co-Parenting co-owners stay on the dashboard. Other asset types are unchanged. */
function skipsCoOwnerBuyProcessRedirect() {
    const raw = String(
        localStorage.getItem('assetType') || localStorage.getItem('AssetType') || ''
    ).trim().toLowerCase().replace(/[\s-]+/g, '');
    return raw === 'marriedparents' || raw === 'marriedparent' || raw === 'coparenting';
}

export function isManageOwnedAssetsModule(module) {
    if (module == null) {
        return false;
    }

    const moduleCode = module.moduleCode != null ? String(module.moduleCode) : '';
    if (moduleCode === MANAGE_OWNED_ASSETS_MODULE_CODE) {
        return true;
    }

    const route = normalizeRoute(module.moduleRoute);
    if (route === MANAGE_OWNED_ASSETS_ROUTE || route.includes(MANAGE_OWNED_ASSETS_ROUTE)) {
        return true;
    }

    const moduleName = normalizeText(module.moduleName);
    if (
        moduleName === 'Asset Buy Process' ||
        moduleName.includes('Asset Buy Process') ||
        moduleName === 'assetDocumentProcess'
    ) {
        return true;
    }

    return false;
}

function findModuleInTree(modules, predicate) {
    if (!Array.isArray(modules) || modules.length === 0) {
        return null;
    }

    for (let i = 0; i < modules.length; i++) {
        const item = modules[i];
        if (item == null) {
            continue;
        }
        if (predicate(item)) {
            return item;
        }

        const fromSub = findModuleInTree(item.subModules, predicate);
        if (fromSub) {
            return fromSub;
        }

        const fromCategory = findModuleInTree(item.categoryModules, predicate);
        if (fromCategory) {
            return fromCategory;
        }
    }

    return null;
}

function toStandaloneMenuItem(module) {
    if (module == null) {
        return null;
    }
    return {
        ...module,
        subModules: null,
        categoryModules: null
    };
}

export function filterPermissionModulesForCoOwner(modules, restrictToOwnedAssetsOnly) {
    if (!restrictToOwnedAssetsOnly) {
        return Array.isArray(modules) ? modules : [];
    }
    if (!Array.isArray(modules)) {
        return [];
    }
    return modules.filter((item) => isManageOwnedAssetsModule(item));
}

export function filterSidebarTreeForCoOwner(modules, restrictToOwnedAssetsOnly) {
    if (!restrictToOwnedAssetsOnly) {
        return Array.isArray(modules) ? modules : [];
    }
    if (!Array.isArray(modules)) {
        return [];
    }

    const ownedAssetsModule = findModuleInTree(modules, isManageOwnedAssetsModule);
    const standalone = toStandaloneMenuItem(ownedAssetsModule);
    return standalone ? [standalone] : [];
}

function readOwnedAssetsCache() {
    try {
        const raw = sessionStorage.getItem(CACHE_STORAGE_KEY);
        if (!raw) {
            return null;
        }
        const parsed = JSON.parse(raw);
        const userId = localStorage.getItem('userId');
        const companyId = localStorage.getItem('companyId');
        if (
            parsed?.userId !== userId ||
            parsed?.companyId !== companyId ||
            typeof parsed?.hasRecords !== 'boolean'
        ) {
            return null;
        }
        return parsed.hasRecords;
    } catch {
        return null;
    }
}

function writeOwnedAssetsCache(hasRecords) {
    try {
        sessionStorage.setItem(
            CACHE_STORAGE_KEY,
            JSON.stringify({
                userId: localStorage.getItem('userId'),
                companyId: localStorage.getItem('companyId'),
                hasRecords: !!hasRecords,
                checkedAt: Date.now()
            })
        );
    } catch {
        // ignore storage errors
    }
}

export function clearCoOwnerOwnedAssetsCache() {
    try {
        sessionStorage.removeItem(CACHE_STORAGE_KEY);
    } catch {
        // ignore storage errors
    }
}

export async function coOwnerHasOwnedAssets(options = {}) {
    const forceRefresh = options.forceRefresh === true;
    if (!forceRefresh) {
        const cached = readOwnedAssetsCache();
        if (cached != null) {
            return cached;
        }
    }

    const userId = localStorage.getItem('userId');
    const companyId = localStorage.getItem('companyId');
    if (!userId || !companyId) {
        writeOwnedAssetsCache(false);
        return false;
    }

    const searchModel = {
        searchItem: null,
        status: '1',
        sortColumn: 'ModifiedOn',
        sortDir: 'desc',
        pageIndex: 0,
        pageSize: 1,
        userId,
        companyId
    };

    try {
        const response = await companyUserService.GetOwnedPropertList(searchModel);
        const rows = response?.data?.data;
        let hasRecords = false;

        if (Array.isArray(rows) && rows.length > 0) {
            const totalRecord = rows[0]?.TotalRecord ?? rows[0]?.totalRecord;
            if (totalRecord != null && totalRecord !== '') {
                hasRecords = Number(totalRecord) > 0;
            } else {
                hasRecords = true;
            }
        }

        writeOwnedAssetsCache(hasRecords);
        return hasRecords;
    } catch {
        // Fail open: keep existing menu if availability check fails.
        return true;
    }
}

/**
 * First owned asset from Manage Owned Vacation Homes
 * (same source as ownedPropertiesList: AssetID + ShareId).
 * Used for co-owner dashboard navigation (share history, booking quick actions).
 * @returns {Promise<{ assetId: string, shareId: string|null } | null>}
 */
export async function getCoOwnerFirstOwnedAsset() {
    const userId = localStorage.getItem('userId');
    const companyId = localStorage.getItem('companyId');
    if (!userId || !companyId) {
        return null;
    }

    const searchModel = {
        searchItem: null,
        status: '1',
        sortColumn: 'ModifiedOn',
        sortDir: 'desc',
        pageIndex: 0,
        pageSize: 1,
        userId,
        companyId
    };

    try {
        const response = await companyUserService.GetOwnedPropertList(searchModel);
        const rows = response?.data?.data;
        if (!Array.isArray(rows) || !rows.length) {
            return null;
        }
        const row = rows[0];
        const assetId = row.AssetID ?? row.AssetId ?? row.assetID ?? row.assetId;
        const shareId = row.ShareId ?? row.shareId ?? row.ShareID ?? row.shareID;
        if (assetId == null || assetId === '') {
            return null;
        }
        return {
            assetId: String(assetId),
            shareId: shareId != null && shareId !== '' ? String(shareId) : null
        };
    } catch {
        return null;
    }
}

/**
 * AssetId from Manage Owned Vacation Homes (same source as ownedPropertiesList.AssetID).
 * Used for co-owner dashboard navigation to share history.
 */
export async function getCoOwnerOwnedAssetId() {
    const owned = await getCoOwnerFirstOwnedAsset();
    return owned?.assetId ?? null;
}

export async function shouldRestrictCoOwnerMenu(options = {}) {
    if (!isCoOwnerUserType() || skipsCoOwnerBuyProcessRedirect()) {
        return false;
    }
    const hasRecords = await coOwnerHasOwnedAssets(options);
    return !hasRecords;
}

/**
 * Sync check using session cache (populated during login / owned-assets list load).
 */
export function shouldRestrictCoOwnerMenuSync() {
    if (!isCoOwnerUserType() || skipsCoOwnerBuyProcessRedirect()) {
        return false;
    }
    const cached = readOwnedAssetsCache();
    return cached === false;
}

export function isDashboardRoute(pathOrRoute) {
    if (pathOrRoute == null) {
        return false;
    }
    const path =
        typeof pathOrRoute === 'string'
            ? pathOrRoute
            : pathOrRoute.path || pathOrRoute.fullPath || '';
    const normalized = normalizeRoute(String(path).trim().toLowerCase());
    if (!normalized) {
        return false;
    }
    if (normalized === 'dashboard' || normalized.startsWith('dashboard/')) {
        return true;
    }
    const name =
        pathOrRoute && typeof pathOrRoute === 'object' && pathOrRoute.name != null
            ? String(pathOrRoute.name).trim().toLowerCase()
            : '';
    return name === 'dashboard';
}

export function getManageOwnedAssetsPostLoginRoute() {
    try {
        const raw = localStorage.getItem('moduleList');
        if (raw) {
            const modules = JSON.parse(raw);
            const ownedModule = findModuleInTree(
                Array.isArray(modules) ? modules : [],
                isManageOwnedAssetsModule
            );
            const route = ownedModule?.moduleRoute;
            if (route) {
                const trimmed = String(route).trim().replace(/^\/+/, '');
                return trimmed ? `/${trimmed}` : MANAGE_OWNED_ASSETS_POST_LOGIN_ROUTE;
            }
        }
    } catch {
        // fall through to default route
    }
    return MANAGE_OWNED_ASSETS_POST_LOGIN_ROUTE;
}

export async function resolvePostLoginRedirectPath(defaultPath = '/dashboard') {
    if (isCoOwnerUserType() && skipsCoOwnerBuyProcessRedirect()) {
        return '/dashboard';
    }
    if (!isCoOwnerUserType()) {
        return defaultPath;
    }
    const restrict = await shouldRestrictCoOwnerMenu({ forceRefresh: false });
    if (restrict) {
        if (String(defaultPath || '').startsWith('/assetDocumentProcess')) {
            return defaultPath;
        }
        return getManageOwnedAssetsPostLoginRoute();
    }
    return defaultPath;
}

export async function applyCoOwnerMenuPolicyToPermissions(modules, options = {}) {
    const list = Array.isArray(modules) ? modules : [];
    if (!isCoOwnerUserType()) {
        return list;
    }

    const restrict = await shouldRestrictCoOwnerMenu(options);
    return filterPermissionModulesForCoOwner(list, restrict);
}

export async function applyCoOwnerMenuPolicyToSidebarTree(modules, options = {}) {
    const list = Array.isArray(modules) ? modules : [];
    if (!isCoOwnerUserType()) {
        return list;
    }

    const restrict = await shouldRestrictCoOwnerMenu(options);
    return filterSidebarTreeForCoOwner(list, restrict);
}

export async function persistUserModulePermissions(modules, options = {}) {
    const filtered = await applyCoOwnerMenuPolicyToPermissions(modules, options);
    localStorage.setItem('moduleList', JSON.stringify(filtered));
    return filtered;
}

export function notifyCoOwnerMenuUpdated() {
    if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent(MENU_UPDATED_EVENT));
    }
}

export async function refreshCoOwnerMenuState(options = {}) {
    if (!isCoOwnerUserType()) {
        return null;
    }

    if (options.forceRefresh !== false) {
        clearCoOwnerOwnedAssetsCache();
    }

    const restrict = await shouldRestrictCoOwnerMenu({ forceRefresh: true });

    try {
        const userId = localStorage.getItem('userId');
        if (userId) {
            const response = await userAuthService.getUserModulePermissions(userId, '');
            const sourceModules = Array.isArray(response?.data) ? response.data : [];
            const filtered = filterPermissionModulesForCoOwner(sourceModules, restrict);
            localStorage.setItem('moduleList', JSON.stringify(filtered));
        }
    } catch {
        try {
            const raw = localStorage.getItem('moduleList');
            if (raw) {
                const parsed = JSON.parse(raw);
                const filtered = filterPermissionModulesForCoOwner(
                    Array.isArray(parsed) ? parsed : [],
                    restrict
                );
                localStorage.setItem('moduleList', JSON.stringify(filtered));
            }
        } catch {
            // ignore parse errors
        }
    }

    notifyCoOwnerMenuUpdated();
    return { restrictToOwnedAssetsOnly: restrict };
}

export const CO_OWNER_MENU_UPDATED_EVENT = MENU_UPDATED_EVENT;
export {
    MANAGE_OWNED_ASSETS_POST_LOGIN_ROUTE,
    MANAGE_OWNED_ASSETS_MODULE_CODE
};
